/* Surfboard Studio — pattern browser for surfboards (sections/surfboard-studio.liquid) */
(() => {
  if (customElements.get('surfboard-studio')) return;

  const WIDTHS = [480, 720, 960, 1200, 1500, 1800, 2400];
  const withWidth = (src, w) => (src ? `${src}${src.includes('?') ? '&' : '?'}width=${w}` : '');
  const srcset = (src) => WIDTHS.map((w) => `${withWidth(src, w)} ${w}w`).join(', ');
  const slug = (s) =>
    String(s)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  const esc = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  class SurfboardStudio extends HTMLElement {
    connectedCallback() {
      this.products = JSON.parse(this.querySelector('[data-sbs-products]').textContent);
      this.facets = JSON.parse(this.querySelector('[data-sbs-facets]').textContent);
      this.byHandle = new Map(this.products.map((p) => [p.handle, p]));
      this.infoCache = new Map();
      this.selected = {}; // option name -> value (kept across patterns)
      this.active = {}; // facet key -> Set of value slugs
      this.facets.forEach((f) => (this.active[f.key] = new Set()));
      this.t = this.dataset;

      this.$ = (sel) => this.querySelector(sel);
      this.$$ = (sel) => Array.from(this.querySelectorAll(sel));

      this.heroes = { a: this.$('[data-sbs-hero="a"]'), b: this.$('[data-sbs-hero="b"]') };
      this.front = 'a';
      this.strip = this.$('[data-sbs-strip]');
      this.thumbs = new Map(this.$$('.sbs-thumb').map((el) => [el.dataset.handle, el]));

      this.buildFacetIndex();
      this.readUrl();
      this.bind();
      this.applyFilters({ keepCurrent: true, initial: true });
      this.loadMorePages();
    }

    /* ---------- more than 250 products: fetch the other paginate pages of this section ---------- */
    async loadMorePages() {
      const pages = Number(this.dataset.pages || 1);
      if (pages < 2) return;
      this.classList.add('is-loading-more');
      for (let pg = 2; pg <= pages; pg++) {
        try {
          const url = new URL(window.location.pathname, window.location.origin);
          url.searchParams.set('section_id', this.dataset.sectionId);
          url.searchParams.set('page', pg);
          const html = await (await fetch(url, { credentials: 'same-origin' })).text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const data = doc.querySelector('[data-sbs-products]');
          if (!data) continue;
          const added = JSON.parse(data.textContent).filter((p) => !this.byHandle.has(p.handle));
          added.forEach((p) => {
            this.products.push(p);
            this.byHandle.set(p.handle, p);
          });
          const frag = document.createDocumentFragment();
          doc.querySelectorAll('.sbs-thumb').forEach((a) => {
            if (this.thumbs.has(a.dataset.handle)) return;
            const el = document.importNode(a, true);
            this.thumbs.set(el.dataset.handle, el);
            frag.appendChild(el);
          });
          this.strip.appendChild(frag);
        } catch (e) {
          console.error(e);
        }
      }
      this.classList.remove('is-loading-more');
      this.buildFacetIndex();
      const pending = this.pendingHandle && this.byHandle.has(this.pendingHandle) ? this.pendingHandle : null;
      this.pendingHandle = null;
      this.applyFilters({ keepCurrent: true, handle: pending });
    }

    /* ---------- facets ---------- */
    buildFacetIndex() {
      // products without motif data inherit it from a sibling with the same motif code (e.g. "1x8-21C")
      const MOTIF_KEYS = ['room', 'movement', 'style', 'colors', 'category'];
      const hasData = (p) => MOTIF_KEYS.some((k) => (p.f?.[k] || []).length);
      const donors = new Map();
      this.products.forEach((p) => {
        if (p.code && !p.inherited && hasData(p) && !donors.has(p.code)) donors.set(p.code, p);
      });
      this.products.forEach((p) => {
        if (!p.code || !p.f || hasData(p)) return;
        const d = donors.get(p.code);
        if (!d) return;
        MOTIF_KEYS.forEach((k) => (p.f[k] = (d.f[k] || []).slice()));
        if (!(p.f.motif || []).length && (d.f.motif || []).length) p.f.motif = d.f.motif.slice();
        if (!p.motif && d.motif) p.motif = d.motif;
        p.inherited = true;
      });

      this.facetValues = {};
      this.facets.forEach(({ key }) => {
        const map = new Map();
        this.products.forEach((p) =>
          (p.f?.[key] || []).forEach((label) => {
            if (!label) return;
            const s = slug(label);
            if (!map.has(s)) map.set(s, label);
          })
        );
        this.facetValues[key] = [...map.entries()]
          .map(([value, label]) => ({ value, label }))
          .sort((x, y) => x.label.localeCompare(y.label));
      });
      this.products.forEach((p) => {
        p._f = {};
        this.facets.forEach(({ key }) => (p._f[key] = new Set((p.f?.[key] || []).map(slug))));
      });
    }

    matches(p, skipKey) {
      return this.facets.every(({ key }) => {
        if (key === skipKey) return true;
        const set = this.active[key];
        if (!set.size) return true;
        for (const v of set) if (p._f[key].has(v)) return true;
        return false;
      });
    }

    renderFacets() {
      const list = this.$('[data-sbs-facet-list]');
      const openState = {};
      list.querySelectorAll('details[data-key]').forEach((d) => (openState[d.dataset.key] = d.open));

      list.innerHTML = this.facets
        .filter(({ key }) => this.facetValues[key].length)
        .map(({ key, label }) => {
          const base = this.products.filter((p) => this.matches(p, key));
          const chips = this.facetValues[key]
            .map(({ value, label: vl }) => {
              const count = base.filter((p) => p._f[key].has(value)).length;
              const on = this.active[key].has(value);
              return `<button type="button" class="sbs__chip${on ? ' is-on' : ''}" data-facet="${key}" data-value="${value}" aria-pressed="${on}" ${
                !count && !on ? 'disabled' : ''
              }>${esc(vl)}<span class="sbs__chip-count">${count}</span></button>`;
            })
            .join('');
          const n = this.active[key].size;
          const open = key in openState ? openState[key] || n > 0 : n > 0;
          const searchable = this.facetValues[key].length > 12;
          const q = (this.facetQuery && this.facetQuery[key]) || '';
          const search = searchable
            ? `<div class="sbs__facet-search"><input type="search" data-facet-search="${key}" value="${esc(q)}" placeholder="${esc(
                this.t.textSearch || ''
              )}" aria-label="${esc(this.t.textSearch || label)}" autocomplete="off"></div>`
            : '';
          return `<details class="sbs__facet" data-key="${key}" ${open ? 'open' : ''}>
              <summary><span>${esc(label)}</span>${n ? `<span class="sbs__facet-n">${n}</span>` : ''}<span class="sbs__acc-icon" aria-hidden="true"></span></summary>
              ${search}
              <div class="sbs__chips${searchable ? ' sbs__chips--scroll' : ''}" data-chips="${key}">${chips}</div>
            </details>`;
        })
        .join('');

      Object.keys(this.facetQuery || {}).forEach((k) => this.filterChips(k));

      const total = Object.values(this.active).reduce((a, s) => a + s.size, 0);
      this.$$('[data-sbs-clear]').forEach((b) => (b.hidden = !total && !b.closest('[data-sbs-empty]')));
      const badge = this.$('[data-sbs-active-count]');
      badge.hidden = !total;
      badge.textContent = total;
      const apply = this.$('[data-sbs-apply-label]');
      if (apply) apply.textContent = (this.t.textResults || '[count]').replace('[count]', this.list.length);
    }

    filterChips(key) {
      const box = this.querySelector(`[data-chips="${key}"]`);
      if (!box) return;
      const q = slug(this.facetQuery?.[key] || '');
      box.querySelectorAll('.sbs__chip').forEach((c) => {
        const match = !q || c.dataset.value.includes(q) || c.classList.contains('is-on');
        c.hidden = !match;
      });
    }

    applyFilters({ keepCurrent = true, initial = false, handle = null } = {}) {
      const current = this.current;
      this.list = this.products.filter((p) => this.matches(p));

      // strip: show/hide + order follows collection order
      const visible = new Set(this.list.map((p) => p.handle));
      this.thumbs.forEach((el, handle) => (el.hidden = !visible.has(handle)));
      this.renderFacets();

      const empty = this.$('[data-sbs-empty]');
      empty.hidden = this.list.length > 0;
      this.classList.toggle('is-empty', !this.list.length);
      if (!this.list.length) {
        this.$('[data-sbs-counter]').textContent = '';
        this.writeUrl();
        return;
      }

      let idx = 0;
      const wanted = handle || (initial ? this.initialHandle : current?.handle);
      if (initial && wanted && !this.byHandle.has(wanted)) this.pendingHandle = wanted;
      if (keepCurrent && wanted) {
        const i = this.list.findIndex((p) => p.handle === wanted);
        if (i >= 0) idx = i;
      }
      this.show(idx, { instant: initial, force: initial || this.list[idx] !== current });
      this.updateStrip(this.list[idx], initial);
      this.updatePeeks();
      this.writeUrl();
    }

    /* ---------- navigation ---------- */
    go(step) {
      if (!this.list.length) return;
      const n = this.list.length;
      this.show((this.index + step + n) % n, { dir: step });
    }

    show(index, { instant = false, force = false, dir = 0 } = {}) {
      const p = this.list[index];
      if (!p) return;
      const changed = p !== this.current;
      this.index = index;
      if (!changed && !force) return;
      this.current = p;

      this.renderPanel(p);
      this.swapHero(p, instant, dir);
      this.updateStrip(p, instant);
      this.updatePeeks();
      this.renderPhotos(p);
      this.preloadNeighbours();
      this.writeUrl();
      this.scheduleRemote(p);
    }

    heroOf(p, v) {
      return v && v.img ? { src: v.img, r: v.imgR || p.heroR } : { src: p.hero, r: p.heroR };
    }

    swapHero(p, instant, dir) {
      const h = this.heroOf(p, this.variant && p.variants.includes(this.variant) ? this.variant : this.variantFor(p));
      this.heroSrc = h.src;
      if (h.r) this.$('.sbs__frame').style.setProperty('--sbs-frame-ratio', h.r);
      const nextKey = this.front === 'a' ? 'b' : 'a';
      const back = this.heroes[nextKey];
      const front = this.heroes[this.front];
      const sizes = this.$('[data-sbs-stage]').offsetWidth + 'px';
      const token = (this.heroToken = (this.heroToken || 0) + 1);

      if (instant) {
        front.sizes = sizes;
        front.srcset = srcset(h.src);
        front.src = withWidth(h.src, 1200);
        front.alt = p.heroAlt || p.title;
        return;
      }

      back.classList.remove('is-active');
      back.sizes = sizes;
      back.srcset = srcset(h.src);
      back.src = withWidth(h.src, 1200);
      back.alt = p.heroAlt || p.title;
      this.classList.add('is-loading');
      this.dataset.dir = dir > 0 ? 'next' : dir < 0 ? 'prev' : '';

      const done = () => {
        if (token !== this.heroToken) return;
        this.classList.remove('is-loading');
        back.classList.add('is-active');
        front.classList.remove('is-active');
        this.front = nextKey;
      };
      (back.decode ? back.decode() : Promise.resolve()).then(done, done);
    }

    updateStrip(p, instant) {
      this.thumbs.forEach((el, h) => {
        const on = h === p.handle;
        el.classList.toggle('is-active', on);
        if (on) el.setAttribute('aria-current', 'true');
        else el.removeAttribute('aria-current');
      });
      const el = this.thumbs.get(p.handle);
      if (el) {
        const left = el.offsetLeft - this.strip.clientWidth / 2 + el.offsetWidth / 2;
        this.strip.scrollTo({ left, behavior: instant ? 'auto' : 'smooth' });
      }
      const counter = this.$('[data-sbs-counter]');
      counter.textContent = `${this.index + 1} / ${this.list.length}`;
    }

    neighbour(step) {
      const n = this.list.length;
      return this.list[(this.index + step + n) % n];
    }

    updatePeeks() {
      const single = this.list.length < 2;
      this.$$('[data-sbs-prev],[data-sbs-next]').forEach((b) => (b.hidden = single));
      const set = (sel, p) => {
        const el = this.$(sel);
        el.innerHTML = p?.hero ? `<img src="${withWidth(p.hero, 200)}" alt="" loading="lazy">` : '';
      };
      set('[data-sbs-peek-prev]', this.neighbour(-1));
      set('[data-sbs-peek-next]', this.neighbour(1));
      this.$('[data-sbs-prev]').setAttribute('aria-label', this.neighbour(-1)?.title || 'Previous');
      this.$('[data-sbs-next]').setAttribute('aria-label', this.neighbour(1)?.title || 'Next');
    }

    preloadNeighbours() {
      const w = Math.min(2400, Math.ceil(this.$('[data-sbs-stage]').offsetWidth * (window.devicePixelRatio || 1)));
      const target = WIDTHS.find((x) => x >= w) || WIDTHS[WIDTHS.length - 1];
      [1, -1, 2].forEach((s) => {
        const p = this.neighbour(s);
        const h = p && this.heroOf(p, this.variantFor(p));
        if (h?.src) new Image().src = withWidth(h.src, target);
      });
    }

    /* ---------- buy panel ---------- */
    variantFor(p) {
      const want = p.options.map((name, i) => this.selected[name]);
      let v = p.variants.find((v) => v.options.every((o, i) => !want[i] || o === want[i]) && v.available);
      if (!v) v = p.variants.find((v) => v.options.every((o, i) => !want[i] || o === want[i]));
      if (!v) v = p.variants.find((v) => v.id === p.first) || p.variants.find((x) => x.available) || p.variants[0];
      return v;
    }

    renderPanel(p) {
      if (this.initialVariant) {
        const iv = p.variants.find((x) => x.id === this.initialVariant);
        if (iv) p.options.forEach((name, i) => (this.selected[name] = iv.options[i]));
        this.initialVariant = null;
      }
      const v = (this.variant = this.variantFor(p));
      p.options.forEach((name, i) => (this.selected[name] = v.options[i]));

      this.$('[data-sbs-type]').textContent = p.type || '';
      this.$('[data-sbs-title]').textContent = p.motif || p.title;
      const motifEl = this.$('[data-sbs-motif]');
      motifEl.textContent = p.motif ? p.title : '';
      motifEl.hidden = !p.motif;

      this.renderPrice(v);
      this.renderOptions(p, v);
      this.renderAtc(v);

      const d = this.$('[data-sbs-delivery]');
      d.innerHTML = p.days ? esc(this.t.textDelivery || '').replace('[days]', `<strong>${esc(p.days)}</strong>`) : '';
      d.hidden = !p.days;

      const link = this.$('[data-sbs-product-link]');
      link.href = `${p.url}?variant=${v.id}`;

      const tags = this.$('[data-sbs-tags]');
      if (tags) {
        tags.innerHTML = this.facets
          .flatMap(({ key }) =>
            (p.f?.[key] || []).map(
              (l) => `<li><button type="button" class="sbs__tag" data-facet="${key}" data-value="${slug(l)}">${esc(l)}</button></li>`
            )
          )
          .join('');
      }

      this.$('[data-sbs-sticky-title]').textContent = p.motif || p.title;

      // clear remote slots until loaded
      ['price-note', 'notice'].forEach((s) => (this.$(`.sbs__panel [data-sbs-slot="${s}"]`).innerHTML = ''));
      this.$('[data-sbs-details-wrap]').classList.add('is-stale');
    }

    renderPrice(v) {
      const html = v.compare
        ? `<span class="sbs__price-now sbs__price-now--sale">${v.price}</span> <s class="sbs__price-was">${v.compare}</s>`
        : `<span class="sbs__price-now">${v.price}</span>`;
      this.$('[data-sbs-price]').innerHTML = html;
      this.$('[data-sbs-sticky-price]').innerHTML = v.price;
    }

    renderOptions(p, v) {
      const wrap = this.$('[data-sbs-options]');
      if (p.variants.length < 2 && /default/i.test(p.variants[0]?.title || '')) {
        wrap.innerHTML = '';
        return;
      }
      wrap.innerHTML = p.options
        .map((name, i) => {
          const values = [...new Set(p.variants.map((x) => x.options[i]))];
          const pills = values
            .map((val) => {
              const candidate = p.variants.find((x) => x.options[i] === val && x.options.every((o, j) => j === i || o === v.options[j]));
              const avail = candidate ? candidate.available : p.variants.some((x) => x.options[i] === val && x.available);
              const on = v.options[i] === val;
              return `<button type="button" class="sbs__pill${on ? ' is-on' : ''}${avail ? '' : ' is-na'}" data-opt="${i}" data-val="${esc(val)}" aria-pressed="${on}">${esc(val)}</button>`;
            })
            .join('');
          return `<fieldset class="sbs__option"><legend>${esc(name)}: <strong>${esc(v.options[i])}</strong></legend><div class="sbs__pills">${pills}</div></fieldset>`;
        })
        .join('');
    }

    renderAtc(v) {
      const btn = this.$('[data-sbs-atc]');
      const label = this.$('[data-sbs-atc-label]');
      const sticky = this.$('[data-sbs-sticky-atc]');
      const text = v.available ? this.t.textAdd : this.t.textSoldout;
      btn.disabled = !v.available;
      sticky.disabled = !v.available;
      label.textContent = text;
      sticky.textContent = text;
      this.$('[data-sbs-error]').hidden = true;
    }

    selectOption(i, val) {
      const p = this.current;
      this.selected[p.options[i]] = val;
      const v = this.variantFor(p);
      this.variant = v;
      p.options.forEach((name, j) => (this.selected[name] = v.options[j]));
      this.renderPrice(v);
      this.renderOptions(p, v);
      this.renderAtc(v);
      this.$('[data-sbs-product-link]').href = `${p.url}?variant=${v.id}`;
      if (this.heroOf(p, v).src !== this.heroSrc) {
        this.swapHero(p, false, 0);
        this.renderPhotos(p);
      }
      this.writeUrl();
    }

    async addToCart(btn) {
      const v = this.variant;
      if (!v || !v.available || btn.classList.contains('loading')) return;
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const fd = new FormData();
      fd.append('id', v.id);
      fd.append('quantity', 1);
      if (cart) {
        fd.append('sections', cart.getSectionsToRender().map((s) => s.id));
        fd.append('sections_url', window.location.pathname);
        cart.setActiveElement?.(document.activeElement);
      }
      btn.classList.add('loading');
      btn.setAttribute('aria-busy', 'true');
      const err = this.$('[data-sbs-error]');
      err.hidden = true;
      try {
        const res = await fetch(window.routes?.cart_add_url || '/cart/add', {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: fd,
        });
        const data = await res.json();
        if (data.status) {
          err.textContent = data.description || data.message || this.t.textError || 'Error';
          err.hidden = false;
          return;
        }
        if (!cart) {
          window.location = window.routes?.cart_url || '/cart';
          return;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'surfboard-studio', productVariantId: v.id, cartData: data });
        }
        cart.classList.remove('is-empty');
        cart.renderContents(data);
      } catch (e) {
        console.error(e);
      } finally {
        btn.classList.remove('loading');
        btn.removeAttribute('aria-busy');
      }
    }

    /* ---------- photos overlay (on the stage) + lightbox ---------- */
    // group = filename without the last "-<suffix>" (and a trailing "-sr"):
    // "...-1-9-H-ba-P-sr-mi" and "...-1-9-H-ba-P-sr-vis1a" -> "...-1-9-H-ba-P"
    photoGroup(src) {
      const stem = String(src || '')
        .split('/')
        .pop()
        .split('?')[0]
        .replace(/\.[a-z0-9]+$/i, '')
        .replace(/_[0-9a-f]{8}-[0-9a-f-]{27,}$/i, '');
      return stem.replace(/-[a-z0-9]+$/i, '').replace(/-sr$/i, '');
    }

    photosFor(p) {
      // when the current size has its own interior shots (posters), show only those
      const g = this.photoGroup(this.heroSrc);
      const same = g ? p.images.filter((img) => this.photoGroup(img.src) === g) : [];
      return same.length ? same : p.images;
    }

    renderPhotos(p) {
      const imgs = this.photosFor(p);
      this.galleryImages = imgs;
      const n = imgs.length;
      const btn = this.$('[data-sbs-photos-open]');
      btn.hidden = !n;
      this.$('[data-sbs-photos-count]').textContent = n ? n : '';
      this.$('[data-sbs-photos-track]').innerHTML = imgs
        .map(
          (img, i) =>
            `<figure class="sbs__photos-slide" data-pi="${i}">
              <img src="${withWidth(img.src, 1200)}" srcset="${srcset(img.src)}" sizes="(min-width: 1200px) 50vw, 100vw" alt="${esc(img.alt)}" loading="lazy">
            </figure>`
        )
        .join('');
      this.$('[data-sbs-photos-dots]').innerHTML =
        n > 1 ? imgs.map((_, i) => `<button type="button" class="sbs__photos-dot" data-pd="${i}" aria-label="${i + 1} / ${n}"></button>`).join('') : '';
      this.photoIndex = 0;
      this.markPhoto();
      this.closePhotos();
    }

    openPhotos() {
      if (!this.galleryImages?.length) return;
      this.classList.add('photos-open');
      this.$('[data-sbs-photos]').setAttribute('aria-hidden', 'false');
      this.$('[data-sbs-photos-open]').setAttribute('aria-expanded', 'true');
      // eager-load the slides once opened
      this.$$('.sbs__photos-slide img').forEach((im) => (im.loading = 'eager'));
      const marker = (this.t.photosStart || '').trim().toLowerCase();
      const start = marker ? this.galleryImages.findIndex((img) => img.src.toLowerCase().includes(marker)) : -1;
      this.goPhoto(Math.max(0, start), 'auto');
      this.$('[data-sbs-photos-close]').focus({ preventScroll: true });
    }

    closePhotos() {
      if (!this.classList.contains('photos-open')) return;
      this.classList.remove('photos-open');
      this.$('[data-sbs-photos]').setAttribute('aria-hidden', 'true');
      this.$('[data-sbs-photos-open]').setAttribute('aria-expanded', 'false');
    }

    goPhoto(i, behavior = 'smooth') {
      const n = this.galleryImages?.length || 0;
      if (!n) return;
      this.photoIndex = (i + n) % n;
      const track = this.$('[data-sbs-photos-track]');
      track.scrollTo({ left: this.photoIndex * track.clientWidth, behavior });
      this.markPhoto();
    }

    markPhoto() {
      this.$$('[data-pd]').forEach((d) => d.classList.toggle('is-on', Number(d.dataset.pd) === this.photoIndex));
      const single = (this.galleryImages?.length || 0) < 2;
      this.$$('[data-sbs-photos-prev],[data-sbs-photos-next]').forEach((b) => (b.hidden = single));
    }

    openLightbox(i) {
      const lb = this.$('[data-sbs-lightbox]');
      this.lbIndex = i;
      this.setLightbox();
      if (!lb.open) lb.showModal();
    }

    setLightbox() {
      const imgs = this.galleryImages || [];
      if (!imgs.length) return;
      this.lbIndex = (this.lbIndex + imgs.length) % imgs.length;
      const img = imgs[this.lbIndex];
      const el = this.$('[data-sbs-lightbox-img]');
      el.src = withWidth(img.src, 2400);
      el.alt = img.alt;
    }

    /* ---------- remote: info + recommendations ---------- */
    scheduleRemote(p) {
      clearTimeout(this.remoteTimer);
      this.remoteTimer = setTimeout(() => {
        this.loadInfo(p);
      }, 250);
    }

    async loadInfo(p) {
      const view = this.t.infoView;
      let html = this.infoCache.get(p.handle);
      if (!html) {
        try {
          const res = await fetch(`${p.url}?view=${encodeURIComponent(view)}`, { credentials: 'same-origin' });
          html = await res.text();
          this.infoCache.set(p.handle, html);
        } catch (e) {
          return;
        }
      }
      if (this.current !== p) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const root = doc.querySelector('.sbs-info');
      if (!root) return;
      root.querySelectorAll('[data-sbs-slot]').forEach((src) => {
        const target = this.querySelector(`[data-sbs-slot="${src.dataset.sbsSlot}"]`);
        if (!target) return;
        target.innerHTML = src.innerHTML;
        // re-run inline scripts from app blocks
        target.querySelectorAll('script').forEach((old) => {
          const s = document.createElement('script');
          [...old.attributes].forEach((a) => s.setAttribute(a.name, a.value));
          s.textContent = old.textContent;
          old.replaceWith(s);
        });
      });
      this.$('[data-sbs-details-wrap]').classList.remove('is-stale');
      const motif = this.$('[data-sbs-slot="motif"]');
      const motifAcc = this.$('[data-sbs-motif-acc]');
      if (motif && motifAcc) motifAcc.hidden = !motif.textContent.trim();
    }

    /* ---------- URL state ---------- */
    readUrl() {
      const q = new URLSearchParams(window.location.search);
      this.initialHandle = q.get('board') || null;
      this.initialVariant = Number(q.get('variant')) || null;
      this.facets.forEach(({ key }) => {
        const v = q.get(key);
        if (v) v.split(',').forEach((x) => this.active[key].add(x));
      });
    }

    writeUrl() {
      const q = new URLSearchParams(window.location.search);
      this.facets.forEach(({ key }) => {
        const s = this.active[key];
        if (s.size) q.set(key, [...s].join(','));
        else q.delete(key);
      });
      if (this.current && this.list.length) q.set('board', this.current.handle);
      else q.delete('board');
      if (this.variant && this.current?.variants.length > 1) q.set('variant', this.variant.id);
      else q.delete('variant');
      const qs = q.toString();
      history.replaceState(history.state, '', `${window.location.pathname}${qs ? '?' + qs : ''}`);
    }

    /* ---------- filters drawer (mobile) ---------- */
    toggleFilters(open) {
      this.classList.toggle('filters-open', open);
      document.body.classList.toggle('sbs-lock', open);
      this.$('[data-sbs-open-filters]').setAttribute('aria-expanded', open);
    }

    toggleFacet(key, value) {
      const set = this.active[key];
      if (!set) return;
      set.has(value) ? set.delete(value) : set.add(value);
      this.applyFilters({ keepCurrent: true });
    }

    /* ---------- events ---------- */
    bind() {
      this.addEventListener('click', (e) => {
        const t = e.target;
        if (t.closest('[data-sbs-prev]')) return this.go(-1);
        if (t.closest('[data-sbs-next]')) return this.go(1);

        const thumb = t.closest('.sbs-thumb');
        if (thumb) {
          e.preventDefault();
          const i = this.list.findIndex((p) => p.handle === thumb.dataset.handle);
          if (i >= 0) this.show(i, { dir: i > this.index ? 1 : -1 });
          return;
        }

        const chip = t.closest('[data-facet]');
        if (chip) {
          const fromTag = chip.classList.contains('sbs__tag');
          if (fromTag) {
            // tag in buy panel: filter to "patterns like this"
            this.active[chip.dataset.facet]?.add(chip.dataset.value);
            this.applyFilters({ keepCurrent: true });
          } else this.toggleFacet(chip.dataset.facet, chip.dataset.value);
          return;
        }

        if (t.closest('[data-sbs-clear]')) {
          Object.values(this.active).forEach((s) => s.clear());
          return this.applyFilters({ keepCurrent: true });
        }
        if (t.closest('[data-sbs-open-filters]')) return this.toggleFilters(true);
        if (t.closest('[data-sbs-close-filters]')) return this.toggleFilters(false);

        const pill = t.closest('[data-opt]');
        if (pill) return this.selectOption(Number(pill.dataset.opt), pill.dataset.val);

        const atc = t.closest('[data-sbs-atc],[data-sbs-sticky-atc]');
        if (atc) return this.addToCart(atc);

        if (t.closest('[data-sbs-photos-open]')) return this.openPhotos();
        if (t.closest('[data-sbs-photos-close]')) {
          this.closePhotos();
          return this.$('[data-sbs-photos-open]').focus({ preventScroll: true });
        }
        if (t.closest('[data-sbs-photos-prev]')) return this.goPhoto(this.photoIndex - 1);
        if (t.closest('[data-sbs-photos-next]')) return this.goPhoto(this.photoIndex + 1);
        const dot = t.closest('[data-pd]');
        if (dot) return this.goPhoto(Number(dot.dataset.pd));
        if (t.closest('[data-sbs-photos-zoom]')) return this.openLightbox(this.photoIndex);
        const slide = t.closest('[data-pi]');
        if (slide) return this.openLightbox(Number(slide.dataset.pi));

        const more = t.closest('[data-sbs-more-link]');
        if (more) {
          e.preventDefault();
          const wrap = this.$('[data-sbs-details-wrap]');
          const first = wrap.querySelector('details:not([hidden])');
          if (first && !wrap.querySelector('details[open]')) first.open = true;
          wrap.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        if (t.closest('[data-sbs-lb-prev]')) {
          this.lbIndex--;
          return this.setLightbox();
        }
        if (t.closest('[data-sbs-lb-next]')) {
          this.lbIndex++;
          return this.setLightbox();
        }
        const lb = this.$('[data-sbs-lightbox]');
        if (t.closest('[data-sbs-lightbox-close]') || t === lb) return lb.close();
      });

      this.addEventListener('input', (e) => {
        const inp = e.target.closest('[data-facet-search]');
        if (!inp) return;
        this.facetQuery = this.facetQuery || {};
        this.facetQuery[inp.dataset.facetSearch] = inp.value;
        this.filterChips(inp.dataset.facetSearch);
      });

      // keyboard arrows (not while typing / in dialog)
      document.addEventListener('keydown', (e) => {
        if (e.defaultPrevented || e.altKey || e.metaKey || e.ctrlKey) return;
        const lb = this.$('[data-sbs-lightbox]');
        if (lb.open) {
          if (e.key === 'ArrowLeft') (this.lbIndex--, this.setLightbox());
          if (e.key === 'ArrowRight') (this.lbIndex++, this.setLightbox());
          return;
        }
        if (/input|select|textarea/i.test(document.activeElement?.tagName || '')) return;
        if (document.activeElement?.isContentEditable) return;
        if (e.key === 'Escape' && this.classList.contains('filters-open')) return this.toggleFilters(false);
        if (this.classList.contains('photos-open')) {
          if (e.key === 'Escape') return this.closePhotos();
          if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            return this.goPhoto(this.photoIndex + (e.key === 'ArrowLeft' ? -1 : 1));
          }
        }
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        const r = this.$('[data-sbs-stage]').getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) return; // stage not visible
        e.preventDefault();
        this.go(e.key === 'ArrowLeft' ? -1 : 1);
      });

      // swipe on stage
      const stage = this.$('[data-sbs-stage]');
      let sx = 0,
        sy = 0,
        tracking = false;
      stage.addEventListener(
        'touchstart',
        (e) => {
          if (e.touches.length !== 1 || this.classList.contains('photos-open')) return;
          sx = e.touches[0].clientX;
          sy = e.touches[0].clientY;
          tracking = true;
        },
        { passive: true }
      );
      stage.addEventListener(
        'touchend',
        (e) => {
          if (!tracking) return;
          tracking = false;
          const dx = e.changedTouches[0].clientX - sx;
          const dy = e.changedTouches[0].clientY - sy;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.3) this.go(dx < 0 ? 1 : -1);
        },
        { passive: true }
      );

      const track = this.$('[data-sbs-photos-track]');
      track.addEventListener(
        'scroll',
        () => {
          clearTimeout(this.trackTimer);
          this.trackTimer = setTimeout(() => {
            const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
            if (i !== this.photoIndex) {
              this.photoIndex = i;
              this.markPhoto();
            }
          }, 80);
        },
        { passive: true }
      );

      // sticky bar when main ATC is out of view (mobile)
      const sticky = this.$('[data-sbs-sticky]');
      const atc = this.$('[data-sbs-atc]');
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(
          ([entry]) => {
            const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            sticky.classList.toggle('is-visible', show);
            sticky.setAttribute('aria-hidden', !show);
            this.$('[data-sbs-sticky-atc]').tabIndex = show ? 0 : -1;
          },
          { threshold: 0 }
        ).observe(atc);
      }

      window.addEventListener(
        'resize',
        () => {
          clearTimeout(this.rsz);
          this.rsz = setTimeout(() => this.current && this.updateStrip(this.current, true), 150);
        },
        { passive: true }
      );
    }
  }

  customElements.define('surfboard-studio', SurfboardStudio);
})();
